import React from 'react';
import { Box, Typography, Stack, Chip, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MailOfferReviewStatus } from '../../../models/mailOfferReview';

interface MailDetailsViewProps {
  mail: {
    from: string;
    subject?: string;
    body: string;
    messageId?: string;
    inReplyTo?: string;
    status?: MailOfferReviewStatus;
  };
  showMessageId?: boolean;
  bodyMaxHeight?: number | string;
}

const MailDetailsView: React.FC<MailDetailsViewProps> = ({ 
  mail, 
  showMessageId = false,
  bodyMaxHeight = 400
}) => {
  const { t } = useTranslation();

  const getStatusDisplay = (status: MailOfferReviewStatus) => {
    switch (status) {
      case MailOfferReviewStatus.CREATED:
        return { label: t('Created'), color: 'success' as const, description: t('Offers have been successfully created') };
      case MailOfferReviewStatus.REVIEW:
        return { label: t('Review'), color: 'warning' as const, description: t('Ready for your review') };
      case MailOfferReviewStatus.DETECTED:
        return { label: t('Detected'), color: 'info' as const, description: t('Offers detected, extraction pending') };
      case MailOfferReviewStatus.EMPTY:
        return { label: t('Empty'), color: 'default' as const, description: t('No offers found in this email') };
      case MailOfferReviewStatus.PENDING:
      default:
        return { label: t('Pending'), color: 'default' as const, description: t('Awaiting analysis') };
    }
  };

  return (
    <Stack spacing={2}>
      {mail.status !== undefined && (
        <Box>
          <Typography variant="h6" sx={{ color: 'text.dark', fontWeight: 600 }}>
            {t('Status')}
          </Typography>
          {(() => {
            const statusDisplay = getStatusDisplay(mail.status as MailOfferReviewStatus);
            return (
              <Tooltip title={statusDisplay.description}>
                <Chip label={statusDisplay.label} color={statusDisplay.color} size="small" />
              </Tooltip>
            );
          })()}
        </Box>
      )}

      <Box>
        <Typography variant="h6" sx={{ color: 'text.dark', fontWeight: 600}}>
          {t('From')}
        </Typography>
        <Typography variant="body2">{mail.from}</Typography>
      </Box>

      <Box>
        <Typography variant="h6" sx={{ color: 'text.dark', fontWeight: 600}}>
          {t('Subject')}
        </Typography>
        <Typography variant="body2">{mail.subject || '(no subject)'}</Typography>
      </Box>

      {showMessageId && mail.messageId && (
        <Box>
          <Typography variant="h6" sx={{ color: 'text.dark', fontWeight: 600}}>
            {t('Message ID')}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', wordBreak: 'break-all' }}>
            {mail.messageId}
          </Typography>
        </Box>
      )}

      {showMessageId && mail.inReplyTo && (
        <Box>
          <Typography variant="h6" sx={{ color: 'text.dark', fontWeight: 600}}>
            {t('In Reply To')}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', wordBreak: 'break-all' }}>
            {mail.inReplyTo}
          </Typography>
        </Box>
      )}

      <Box>
        <Typography variant="h6" sx={{ color: 'text.dark', fontWeight: 600}}>
          {t('Body')}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            p: 1.5,
            bgcolor: 'background.inputGrey',
            borderRadius: 1,
            maxHeight: bodyMaxHeight,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {mail.body}
        </Typography>
      </Box>
    </Stack>
  );
};

export default MailDetailsView;
